import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { productVendor } from 'config/routes'
import { callErrorMsg } from 'helpers/errorMsg'
import { queryParamsURL, serializeQueryToURL } from 'utils/queryParamsURL'
import {
  deleteProductCatalog,
  getProductCatalogs,
  patchPrimaryVendor,
  ProductCatalogItemType,
  ProductCatalogPaginationType,
} from 'utils/apiList/productVendor'
import { toastSuccess } from 'utils/toast'
import { withLoadingReducer } from 'utils/reducerHandler'
import { getProduct } from 'utils/api'

interface ProductVendorQueryType {
  pageIndex: number
  [key: string]: unknown
}

interface PaginationStateType {
  pageSize: number
  currentItem: number
  currentPage: number
  totalData: number
  last: boolean
}

interface ProductVendorStateType {
  isLoading: boolean
  isEditPreference: boolean
  product: { product_name: string } | null
  query: ProductVendorQueryType
  pagination: PaginationStateType | Record<string, never>
  data: ProductCatalogItemType[]
  selectedVendor: string | number | null
}

const initialState: ProductVendorStateType = {
  isLoading: false,
  isEditPreference: false,
  product: null,
  query: {
    pageIndex: 1,
  },
  pagination: {},
  data: [],
  selectedVendor: null,
}

export const actSetQuery =
  (query: Record<string, unknown> = {}) =>
  (dispatch: (action: unknown) => unknown) => {
    dispatch(setQuery(query))
    dispatch(fetchGetProductVendor(query))
  }

export const fetchGetProductDetail = createAsyncThunk(
  'productVendorSlice/fetchGetProductDetail',
  async (_, { rejectWithValue }) => {
    try {
      const { productId } = queryParamsURL.getParams(productVendor)
      const response = await getProduct(productId)
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchGetProductVendor = createAsyncThunk(
  'productVendorSlice/fetchGetProductVendor',
  async (params: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const qs = serializeQueryToURL(params)
      const { productId } = queryParamsURL.getParams(productVendor)
      const response = await getProductCatalogs(productId, qs)
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchPatchPrimaryVendor = createAsyncThunk(
  'productVendorSlice/fetchPatchPrimaryVendor',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const { productId } = queryParamsURL.getParams(productVendor)
      const { productVendor: state } = getState() as { productVendor: ProductVendorStateType }
      const { selectedVendor } = state

      const response = await patchPrimaryVendor(productId, selectedVendor)
      dispatch(actSetQuery())
      toastSuccess(response.data.message)
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchDeleteProductVendor = createAsyncThunk(
  'productVendorSlice/fetchDeleteProductVendor',
  async (catalogId: string | number, { rejectWithValue, dispatch }) => {
    try {
      const { productId } = queryParamsURL.getParams(productVendor)
      const response = await deleteProductCatalog(productId, catalogId)
      dispatch(actSetQuery())
      toastSuccess(response.data.message)
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

const productVendorSlice = createSlice({
  name: 'productVendorSlice',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.query = { ...state.query, ...action.payload }
    },
    setIsEditPreference: (state, action: PayloadAction<boolean>) => {
      state.isEditPreference = action.payload
    },
    setSelectedVendor: (state, action: PayloadAction<string | number | null>) => {
      state.selectedVendor = action.payload
    },
  },
  extraReducers: withLoadingReducer(
    (builder) => {
      builder
        .addCase(
          fetchGetProductVendor.fulfilled,
          (
            state,
            action: PayloadAction<{
              data: ProductCatalogItemType[]
              pagination: ProductCatalogPaginationType
            }>,
          ) => {
            const {
              data,
              pagination: { pageSize, pageIndex, totalPages, totalElements, numberOfElements },
            } = action.payload

            state.data = data
            state.pagination = {
              pageSize,
              currentItem: numberOfElements,
              currentPage: pageIndex,
              totalData: totalElements,
              last: pageIndex === totalPages,
            }
            state.selectedVendor = data[0]?.catalogId || null
          },
        )
        .addCase(
          fetchGetProductDetail.fulfilled,
          (state, action: PayloadAction<{ product_name: string }>) => {
            state.product = action.payload
          },
        )
        .addCase(fetchPatchPrimaryVendor.fulfilled, (state) => {
          state.isEditPreference = false
        })
    },
    [
      fetchGetProductDetail,
      fetchGetProductVendor,
      fetchPatchPrimaryVendor,
      fetchDeleteProductVendor,
    ],
  ),
})

export const { setQuery, setIsEditPreference, setSelectedVendor } = productVendorSlice.actions
export default productVendorSlice.reducer
